import React, { useEffect, useState, Suspense } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';

import Head from './layouts/Head';
import LoadingDashboardContent from '../components/LoadingDashboardContent';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_URL, APP_URL } from '../constants';
import OrderTable from '../components/dashboard/OrderTable';
import ProductPerformance from '../components/dashboard/ProductPerformance';
import SalesPerformanceChart from '../components/dashboard/SalesPerformanceChart';
import { useTokens } from '../context/TokenContext';
import { useSiteData } from '../hooks/provider/SiteDataProvider';

const getDashboardDataParsed = (dashboardData) => {
  const statIcons = {
    Customers: 'bi-people-fill',
    Merchants: 'bi-shop',
    Orders: 'bi-box-seam',
    'Total Collection': 'bi-cash-coin',
  };

  const statColors = {
    Customers: 'primary',
    Merchants: 'success',
    Orders: 'info',
    'Total Collection': 'warning',
  };

  const statusIcons = {
    'Pending Verifications': 'bi-shield-exclamation',
    'Pending Products Approvals': 'bi-hourglass-split',
    'Appealed Disputes': 'bi-exclamation-diamond',
  };

  const statusColors = {
    'Pending Verifications': 'warning',
    'Pending Products Approvals': 'primary',
    'Appealed Disputes': 'danger',
  };

  const stats = dashboardData?.overviewStats?.map((item) => {
    const changeType = item.direction === 'increase' ? 'up' : 'down';
    const formattedValue = item.currency
      ? `${item.currency}${item.value.toLocaleString()}`
      : item.value.toLocaleString();

    return {
      title: item.title,
      value: formattedValue,
      change: item.change,
      changeType,
      icon: statIcons[item.title] || 'bi-bar-chart',
      color: statColors[item.title] || 'secondary',
      progress: Math.min(100, Math.floor(Math.random() * 50 + 50)),
    };
  }) || [];

  const statuses = dashboardData?.actionStats?.map((item) => ({
    title: item.title,
    value: item.count,
    description: item.note,
    icon: statusIcons[item.title] || 'bi-info-circle',
    color: statusColors[item.title] || 'secondary',
    trend: item.note.toLowerCase().includes('decrease') ? 'down' : 'up',
  })) || [];

  return { stats, statuses };
};

const Dashboard = () => {
  const { getItem } = useTokens();
  const vendorAuthToken = getItem('vendor', 'AuthToken');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [progress, setProgress] = useState(0);
  const { siteData } = useSiteData();

  const { stats, statuses } = getDashboardDataParsed(dashboardData || {});

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 80) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 200);

    return () => clearInterval(progressInterval);
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/dashboard`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${vendorAuthToken}`,
        },
      });
      setDashboardData(response.data || {});
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  return loading ? (
    <LoadingDashboardContent progress={progress} />
  ) : (
    <>
      <Head title={"Admin Dashboard"} />
      <Container fluid className="py-0">
        <Row className="g-4 pb-4">
          {siteData.admin_notice_board != '' &&
            <div className='col-md-12'>
              <div className="alert alert-info" role="alert">
                <div dangerouslySetInnerHTML={{ __html: siteData.admin_notice_board }}></div>
              </div>
            </div>
          }
          {stats.map((stat, idx) => (
            <Col xl={3} md={6} key={idx}>
              <Card className={`stats-card shadow-sm border-start border-4 border-${stat.color} shadow rounded-4`}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="text-muted text-uppercase fw-bold small">{stat.title}</div>
                      <div className={`stat-value text-${stat.color} fs-4 fw-semibold`}>{stat.value}</div>
                      {/* <div className={`stat-change text-${stat.changeType === 'up' ? 'success' : 'danger'} small`}>
                        <i className={`bi bi-arrow-${stat.changeType}`}></i> <span>{stat.change}</span>
                      </div> */}
                    </div>
                    <div className="icon-circle bg-light rounded-circle p-3">
                      <i className={`bi ${stat.icon} text-${stat.color} fs-4`}></i>
                    </div>
                  </div>
                  <div className="progress mt-4" style={{ height: '6px' }}>
                    <div
                      className={`progress-bar bg-${stat.color}`}
                      role="progressbar"
                      style={{ width: `${stat.progress}%` }}
                    ></div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-4 pb-4">
          {statuses.map((status, idx) => (
            <Col md={4} key={idx}>
              <Card className="shadow rounded-4">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <i className={`bi ${status.icon} text-${status.color} fs-4`}></i>
                      <h6 className="mb-0">{status.title}</h6>
                    </div>
                    {status.trend && (
                      <span className={`text-${status.trend === 'down' ? 'danger' : 'success'}`}>
                        <i className={`bi bi-arrow-${status.trend}`}></i>
                      </span>
                    )}
                  </div>
                  <h3 className="fw-bold text-dark">{status.value}</h3>
                  <p className="text-muted small mb-0">{status.description}</p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="mb-4">
          {/* Low Stock Alerts */}
          {dashboardData?.lowStockProducts && dashboardData.lowStockProducts.length > 0 && (
            <Col md={12} className="mb-4">
              <Card className="shadow rounded-4 border-start border-4 border-danger">
                <Card.Header className="bg-danger bg-opacity-10">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-exclamation-triangle-fill text-danger fs-4"></i>
                      <h5 className="mb-0 text-danger">Low Stock Alerts</h5>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <span className="badge bg-danger">{dashboardData.lowStockProducts.length} Products</span>
                      <a href={`${APP_URL}vendor/product?page=1&limit=20&searchBy=stockless_then&searchValue=${dashboardData.lowStockThreshold}`} className="btn btn-sm btn-outline-danger">
                        <i className="bi bi-list"></i> Show all
                      </a>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3">
                    The following products have stock levels at or below the threshold of <strong>{dashboardData.lowStockThreshold}</strong> units:
                  </p>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Product</th>
                          <th>SKU</th>
                          <th>Stock</th>
                          <th>Price</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.lowStockProducts.slice(0,10).map((product) => (
                          <tr key={product.id} className={product.stock <= 5 ? 'table-danger' : 'table-warning'}>
                            <td>{product.id}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {product.stock <= 5 && <i className="bi bi-exclamation-circle-fill text-danger"></i>}
                                <span className="fw-semibold">{product.title}</span>
                              </div>
                            </td>
                            <td>{product.sku || 'N/A'}</td>
                            <td>
                              <span className={`badge ${product.stock <= 5 ? 'bg-danger' : 'bg-warning'}`}>
                                {product.stock} units
                              </span>
                            </td>
                            <td>₹{product.price}</td>
                            <td>
                              <Link to={`/vendor/product/edit/${product.id}`} className="btn btn-sm btn-outline-primary">
                                <i className="bi bi-pencil"></i> Edit
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )}

          <Col lg={8} md={12}>
            <Card className="card-raised shadow rounded-4 mb-4">
              <Card.Body>
                <SalesPerformanceChart salesData={dashboardData?.salesByPeriod} />
              </Card.Body>
            </Card>

            <Card className="card-raised shadow rounded-4 mb-4">
              <Card.Body>
                <OrderTable userToken={vendorAuthToken} />
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} md={12}>
            <Card className="card-raised shadow rounded-4 mb-4">
              <Card.Body>
                <ProductPerformance userToken={vendorAuthToken} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Dashboard;
